import { Identify, identify, init, setUserId } from '@amplitude/analytics-browser'
import type { Environment } from 'config/environment'

import { getFactorialData } from 'modules/core/lib/cookie'
import { enabledFeatureFlags } from 'modules/core/lib/hasFeature'

type Role = 'manager' | 'admin' | 'employee'

function initAmplitude({ amplitudeEnabled }: Environment) {
  const { isFactorial } = getFactorialData() || {}

  // TODO: Load only if there is consent
  // We are going to initially test the modal for asking consent to Factorial employees.
  // Later we will add the && user.analyticsConsent condition here.
  if (amplitudeEnabled && !isFactorial) {
    window.tracking = true
    init('0ab91ba82cd87b10b641c8ff2b0d87fe', undefined, {
      minIdLength: 1,
    })
  }
}

interface AmplitudeData {
  environment: Environment
  doNotTrack: boolean
  analyticsConsent: boolean | null | undefined
  userId: number
  userFirstName: string
  userLastName: string
  userEmail: string | null | undefined
  userCanRequestUpsell: boolean
  userIsManager: boolean
  userCanAccessWelcomeSpace: boolean
  companyId: number
  companyName: string
  companySize: string
  companyIndustry: string
  companyCountry: string
  subscriptionPlan: string
  subscriptionAtsPlan: string
  subscriptionCurrentCycleQuantity: number | null
  userCanSeeProductUpdates: boolean
  canSelfUpsell: boolean
}

export default function runAmplitude({
  environment,
  doNotTrack,
  analyticsConsent,
  userId,
  userFirstName,
  userLastName,
  userEmail,
  userCanRequestUpsell,
  userIsManager,
  userCanAccessWelcomeSpace,
  companyId,
  companyName,
  companySize,
  companyIndustry,
  companyCountry,
  subscriptionPlan,
  subscriptionAtsPlan,
  subscriptionCurrentCycleQuantity,
  userCanSeeProductUpdates,
  canSelfUpsell,
}: AmplitudeData) {
  // ROLLOUT PHASE 1. Users without explicit consent (value set to null) still get through.
  // Only those with explicit "false" will avoid tracking
  if (analyticsConsent === false) return
  if (doNotTrack) return

  initAmplitude(environment)

  if (!window.tracking) return

  const userLegalName = [userFirstName, userLastName].filter(Boolean).join(' ')

  const role: Role = userCanRequestUpsell ? 'admin' : userIsManager ? 'manager' : 'employee'

  setUserId(String(userId))

  const identity = new Identify()
    .set('user_id', userId)
    .set('user_name', userLegalName)
    .set('user_role_type', role)
    .set('user_email', userEmail ?? '')
    .set('company_id', companyId)
    .set('company_name', companyName)
    .set('company_plan', subscriptionPlan)
    .set('company_ats_plan', subscriptionAtsPlan)
    .set('company_market', companyCountry)
    .set('company_size', companySize)
    .set('company_seats', subscriptionCurrentCycleQuantity ?? '')
    .set('company_industry', companyIndustry)
    .set('is_welcome_space', userCanAccessWelcomeSpace)
    .set('can_see_product_updates', userCanSeeProductUpdates)
    .set('can_self_upsell', canSelfUpsell)

  const featureFlags = enabledFeatureFlags()
  if (featureFlags.length) {
    identity.set('feature_flags', featureFlags)
  }

  identify(identity)
}
